import { pick } from 'lodash';
import { getDisplayValue } from 'shared/utils/dimensionValue';
import { formatDateRange } from 'shared/constants/date';
import { dayjs } from 'shared/utils/date';

export function getSortOrder(sortOrder) {
  if (sortOrder === 'asc') {
    return 'ascend';
  }
  if (sortOrder === 'desc') {
    return 'descend';
  }
  return null;
}

export function getRowId(row, dimensionIds) {
  return Object.values(pick(row, dimensionIds)).map(v => {
    const display = getDisplayValue(v);
    return display != null && display !== '' ? display : 'empty';
  }).join('-');
}

function getDateComparisonResults({ results, segment, dimensionIds, parentData, primaryData, rowId, level }) {
  return (segment.dateRangeSegments || []).map(s => {
    const segmentResult = results.find(r => r.segmentId === s.id);
    const segmentData = segmentResult?.data.find(r => getRowId(r, dimensionIds) === rowId);
    return {
      id: `${rowId}-${s.id}${level ? `-${level}` : ''}`,
      rowId,
      ...(segmentData || {}),
      segmentId: s.id,
      segment: s,
      parentData,
      primaryData,
      level,
    };
  });
}

function getRow({ data, dimensionIds, mainSegment, dateComparisonsAsSegments, comparisonSegments, results, level }) {
  const rowId = getRowId(data, dimensionIds);

  const row = {
    id: `${rowId}-${mainSegment.id}-${level}`,
    rowId,
    ...(data || {}),
    segmentId: mainSegment.id,
    segment: mainSegment,
    level,
  };

  row.dateComparisonResults = getDateComparisonResults({
    results,
    segment: mainSegment,
    dimensionIds,
    parentData: row,
    primaryData: row,
    rowId,
    level,
  });

  row.segmentComparisonResults = comparisonSegments.map(s => {
    const segmentResult = results.find(r => r.segmentId === s.id);
    const segmentData = segmentResult?.data.find(r => getRowId(r, dimensionIds) === rowId);

    const segmentRow = {
      id: `${rowId}-${s.id}-${level}`,
      rowId,
      ...(segmentData || {}),
      segmentId: s.id,
      segment: s,
      parentData: row,
      primaryData: row,
      level,
    };

    segmentRow.dateComparisonResults = getDateComparisonResults({
      results,
      segment: s,
      dimensionIds,
      parentData: segmentRow,
      primaryData: row,
      rowId,
      level,
    });

    return segmentRow;
  });

  const children = [];

  if (dateComparisonsAsSegments) {
    children.push(...row.dateComparisonResults);
  }
  children.push(...row.segmentComparisonResults);
  if (dateComparisonsAsSegments) {
    children.push(...row.segmentComparisonResults.flatMap(s => s.dateComparisonResults));
  }

  if (children.length) {
    row.children = children;
  }

  return row;
}

export function getTotals({ mainSegment, comparisonSegments, results }) {
  const dateComparisonResults = mainSegment.dateRangeSegments.map(s => ({
    ...results?.find(r => r.segmentId === s.id)?.totals,
    id: `totals-${s.id}`,
    segmentId: s.id,
    segment: s,
  }));

  const segmentComparisonResults = comparisonSegments.map(s => ({
    ...results?.find(r => r.segmentId === s.id)?.totals,
    id: `totals-${s.id}`,
    segmentId: s.id,
    segment: s,
    dateComparisonResults: s.dateRangeSegments.map(ss => ({
      id: `totals-${ss.id}`,
      segmentId: ss.id,
      segment: ss,
      ...results?.find(r => r.segmentId === ss.id)?.totals,
    })),
  }));

  return {
    ...results?.find(r => r.segmentId === mainSegment.id)?.totals,
    id: `totals-${mainSegment.id}`,
    segmentId: mainSegment.id,
    segment: mainSegment,
    dateComparisonResults,
    segmentComparisonResults,
    children: [...dateComparisonResults, ...segmentComparisonResults],
  };
}

export function findTotals({ totals, segmentId }) {
  if (totals?.segmentId === segmentId) {
    return totals;
  }

  for (const dateComparisonResult of totals?.dateComparisonResults || []) {
    const result = findTotals({ totals: dateComparisonResult, segmentId });
    if (result) {
      return result;
    }
  }

  for (const segmentComparisonResult of totals?.segmentComparisonResults || []) {
    const result = findTotals({ totals: segmentComparisonResult, segmentId });
    if (result) {
      return result;
    }
  }

  return null;
}

export function getTableData({ dimensions = [], results, mainSegment, comparisonSegments, settings = {}, level }) {
  const primaryResult = results?.find(r => r.segmentId === mainSegment.id);

  if (!primaryResult) {
    return [];
  }

  const dimensionIds = dimensions.map(d => d.name);
  const { dateComparisonsAsSegments } = settings;
  const rows = [];

  for (const data of primaryResult.data) {
    const row = getRow({
      data,
      dimensionIds,
      mainSegment,
      comparisonSegments,
      results,
      dateComparisonsAsSegments,
      level,
    });

    rows.push(row);
  }

  return rows;
}

export function getTableRows(params) {
  return getTableData(params);
}

export function getSwitchableDimensions(dimensions = []) {
  return dimensions.filter(d => d.hidden !== true);
}

export function getLegend({ formatter, tooltipFormatter }) {
  return {
    top: 0,
    padding: [10, 20],
    type: 'scroll',
    selector: [
      {
        type: 'all',
        title: 'All',
      },
      {
        type: 'inverse',
        title: 'Invert',
      },
    ],
    selectorLabel: {
      padding: [4, 8],
      borderRadius: 6,
      borderColor: '#d9d9d9',
    },
    emphasis: {
      selectorLabel: {
        backgroundColor: '#1a2f55',
      },
    },
    tooltip: tooltipFormatter ? {
      show: true,
      formatter: tooltipFormatter,
    } : undefined,
    formatter,
  };
}

export function getSegmentTitle(segment) {
  return `${segment.title} - ${formatDateRange(dayjs, segment.dateRange.period, segment.dateRange.startDate, segment.dateRange.endDate, { week: 'Week' })}`;
}

export function getSegmentHtmlCell(segment) {
  if (!segment?.title) {
    return '';
  }

  const dateRange = segment.dateRange
    ? formatDateRange(dayjs, segment.dateRange.period, segment.dateRange.startDate, segment.dateRange.endDate, { week: 'Week' })
    : '';

  return `<span class="segment-cell"><span class="segment-tag" style="background-color: ${segment.color}; border-color: ${segment.segmentColor || segment.color}">${segment.title}</span>${dateRange ? `<span class="segment-date">${dateRange}</span>` : ''}</span>`;
}

export function getSegmentHtmlTitle(segment) {
  return getSegmentHtmlCell(segment);
}
